<script lang="ts">
  import { onMount } from "svelte";
  import { api, type FunnelData, type ManagerFunnel, type ManagerStats } from "../lib/api";

  let { onback, onhome }: { onback?: () => void; onhome?: () => void } = $props();

  let funnel = $state<FunnelData | null>(null);
  let managers = $state<ManagerStats[]>([]);
  let managerFunnels = $state<ManagerFunnel[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let period = $state<7 | 14 | 30 | 90 | 180 | -1>(30);  // -1 = custom
  let customDays = $state(30);
  let mfLoading = $state(false);

  function moneyShort(rub: number): string {
    if (rub >= 1_000_000) return `${(rub / 1_000_000).toFixed(1).replace(/\.0$/, "")}М`;
    if (rub >= 1000) return `${Math.floor(rub / 1000)}к`;
    return `${rub}`;
  }
  function moneyFull(rub: number): string {
    return moneyShort(rub) + " ₽";
  }

  async function loadMf(days: number) {
    mfLoading = true;
    try {
      const mf = await api.analyticsManagerFunnel(days);
      managerFunnels = mf.managers;
    } catch (e) {
      // soft-fail: keep previous data
    } finally {
      mfLoading = false;
    }
  }

  function setPeriod(p: 7 | 14 | 30 | 90 | 180) {
    period = p;
    loadMf(p);
  }
  function applyCustom() {
    const n = Math.max(1, Math.min(720, Math.floor(customDays || 30)));
    customDays = n;
    period = -1;
    loadMf(n);
  }

  const periodDays = $derived(period === -1 ? customDays : period);
  const periodLabel = $derived(
    period === 7 ? "7 дней" :
    period === 14 ? "2 недели" :
    period === 30 ? "30 дней" :
    period === 90 ? "квартал" :
    period === 180 ? "полгода" :
    `${customDays} дн`
  );

  onMount(async () => {
    try {
      const [f, m, mf] = await Promise.all([
        api.analyticsFunnel(),
        api.analyticsManagers(),
        api.analyticsManagerFunnel(period === -1 ? customDays : period),
      ]);
      funnel = f;
      managers = m.managers;
      managerFunnels = mf.managers;
    } catch (e) {
      error = e instanceof Error ? e.message : "ошибка";
    } finally {
      loading = false;
    }
  });

  // Filter the main pipeline (Воронка) and compute max for bar widths
  const mainPipeline = $derived(funnel?.pipelines.find((p) => p.is_main) ?? funnel?.pipelines[0] ?? null);
  const activeStages = $derived(
    mainPipeline?.stages.filter((s) => !s.is_closed) ?? []
  );
  const maxValue = $derived(Math.max(1, ...activeStages.map((s) => s.value_rub)));
  const maxCount = $derived(Math.max(1, ...activeStages.map((s) => s.count)));

  const maxMgrLoad = $derived(Math.max(1, ...managers.map((m) => m.deal_value_rub)));
</script>

<div class="screen">
  <header class="hdr">
    <button class="back" onclick={() => onback?.()} aria-label="Назад">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
        stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
    </button>
    <div class="title">Аналитика</div>
    {#if onhome}
      <button class="home" onclick={() => onhome?.()} aria-label="Главная">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
          stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
        </svg>
      </button>
    {:else}
      <div class="spacer"></div>
    {/if}
  </header>

  {#if loading}
    <div class="loading">Считаю…</div>
  {:else if error}
    <div class="error-box">{error}</div>
  {:else if funnel}
    <!-- 30-day stats -->
    <section class="totals">
      <div class="t-card success">
        <div class="t-label">выиграно (30 дн)</div>
        <div class="t-val">{funnel.won_30d}</div>
      </div>
      <div class="t-card danger">
        <div class="t-label">проиграно</div>
        <div class="t-val">{funnel.lost_30d}</div>
      </div>
      <div class="t-card accent">
        <div class="t-label">конверсия</div>
        <div class="t-val">{funnel.conversion_30d.toFixed(0)}%</div>
      </div>
    </section>

    {#if mainPipeline}
      <div class="section-title">Воронка «{mainPipeline.name}»</div>
      <section class="funnel">
        {#each activeStages as s}
          <div class="stage">
            <div class="stage-head">
              <span class="s-name">{s.name}</span>
              <span class="s-count">{s.count} · {moneyShort(s.value_rub)} ₽</span>
            </div>
            <div class="bar-wrap">
              <div class="bar value"
                   style:width="{(s.value_rub / maxValue) * 100}%"
                   title="value"></div>
            </div>
            <div class="bar-wrap thin">
              <div class="bar count"
                   style:width="{(s.count / maxCount) * 100}%"
                   title="count"></div>
            </div>
          </div>
        {/each}
      </section>
      <div class="legend">
        <span class="lg lg-value"></span> сумма в стадии &nbsp;
        <span class="lg lg-count"></span> кол-во сделок
      </div>
    {/if}

    {#if managers.length > 0}
      <div class="section-title">Менеджеры — сравнение</div>
      <section class="mgrs">
        {#each managers as m}
          <a class="mgr-card" href={`?manager=${m.amo_id}`}>
            <div class="mgr-head">
              <span class="m-name">{m.name}</span>
              {#if m.is_admin}<span class="m-badge">admin</span>{/if}
            </div>
            <div class="mgr-bar-wrap">
              <div class="mgr-bar" style:width="{(m.deal_value_rub / maxMgrLoad) * 100}%"></div>
            </div>
            <div class="mgr-stats">
              <div class="m-stat">
                <span class="m-stat-val accent">{moneyShort(m.deal_value_rub)}</span>
                <span class="m-stat-lab">в воронке</span>
              </div>
              <div class="m-stat">
                <span class="m-stat-val">{m.active_deals}</span>
                <span class="m-stat-lab">сделок</span>
              </div>
              <div class="m-stat">
                <span class="m-stat-val" class:warn={m.overdue > 0}>{m.overdue}</span>
                <span class="m-stat-lab">просрочек</span>
              </div>
              {#if m.won_30d + m.lost_30d > 0}
                <div class="m-stat">
                  <span class="m-stat-val">{m.conversion_30d.toFixed(0)}%</span>
                  <span class="m-stat-lab">конв. 30д</span>
                </div>
              {/if}
            </div>
          </a>
        {/each}
      </section>
    {/if}

    {#if managerFunnels.length > 0 || mfLoading}
      <div class="section-title">
        Воронка по менеджеру · {periodLabel}
        {#if mfLoading}<span class="loading-pill">обновляю…</span>{/if}
      </div>
      <div class="period-bar">
        <button class="rng" class:active={period === 7} onclick={() => setPeriod(7)}>7 дн</button>
        <button class="rng" class:active={period === 14} onclick={() => setPeriod(14)}>2 нед</button>
        <button class="rng" class:active={period === 30} onclick={() => setPeriod(30)}>30 дн</button>
        <button class="rng" class:active={period === 90} onclick={() => setPeriod(90)}>квартал</button>
        <button class="rng" class:active={period === 180} onclick={() => setPeriod(180)}>полгода</button>
        <div class="custom-wrap">
          <input
            type="number"
            min="1"
            max="720"
            class="custom-input"
            class:active={period === -1}
            bind:value={customDays}
            onchange={applyCustom}
          />
          <span class="custom-unit">дн</span>
        </div>
      </div>
      <p class="hint">Сколько лидов обработал, довёл до КП, до договора. Конверсии на каждом шаге.<br/>
        <span class="hint-note">«конв.» — это % сделок которые уже закрылись Won в этом периоде (won / обработано). 0% не значит плохо — большинство ещё в работе.</span>
      </p>
      <section class="mf-list">
        {#each managerFunnels as m}
          <a class="mf-card" href={`?manager=${m.amo_id}`}>
            <div class="mf-name">{m.name}</div>
            <div class="mf-funnel">
              <div class="mf-row">
                <span class="mf-label">обработал</span>
                <span class="mf-val">{m.funnel.processed}</span>
                <div class="mf-bar"><div class="mf-fill" style:width="100%"></div></div>
              </div>
              <div class="mf-row">
                <span class="mf-label">КП отправил</span>
                <span class="mf-val">{m.funnel.kp_reached}</span>
                <div class="mf-bar"><div class="mf-fill" style:width="{m.funnel.processed > 0 ? (m.funnel.kp_reached / m.funnel.processed) * 100 : 0}%"></div></div>
                {#if m.funnel.processed > 0}
                  <span class="mf-pct">{m.conversion.processed_to_kp.toFixed(0)}%</span>
                {/if}
              </div>
              <div class="mf-row">
                <span class="mf-label">до договора</span>
                <span class="mf-val">{m.funnel.contract_reached}</span>
                <div class="mf-bar"><div class="mf-fill brass" style:width="{m.funnel.processed > 0 ? (m.funnel.contract_reached / m.funnel.processed) * 100 : 0}%"></div></div>
                {#if m.funnel.kp_reached > 0}
                  <span class="mf-pct">{m.conversion.kp_to_contract.toFixed(0)}% c КП</span>
                {/if}
              </div>
              <div class="mf-row mf-row-bottom">
                <span class="mf-stat success">Won {m.funnel.won}</span>
                <span class="mf-stat danger">Lost {m.funnel.lost}</span>
                <span class="mf-stat dim">+ {m.funnel.new_in_period} новых</span>
                {#if m.funnel.won + m.funnel.lost > 0}
                  <span class="mf-stat accent">won/закрытых {m.funnel.won + m.funnel.lost > 0 ? (100 * m.funnel.won / (m.funnel.won + m.funnel.lost)).toFixed(0) : 0}%</span>
                {:else}
                  <span class="mf-stat dim">пока ничего не закрыто</span>
                {/if}
              </div>
            </div>
          </a>
        {/each}
      </section>
    {/if}

    <p class="footer-hint">
      Воронка считается по текущему статусу сделок с активностью за период.
      Точная история переходов (когда стадия именно сменилась) — Phase 2.
    </p>
  {/if}
</div>

<style>
  .screen { padding-bottom: 80px; }
  .hdr {
    padding: calc(12px + var(--safe-top, 0px)) 16px 12px;
    display: flex; align-items: center; gap: 8px;
    border-bottom: 1px solid var(--border);
  }
  .back, .home {
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    color: var(--text-mute);
  }
  .back:active, .home:active { background: var(--bg-elev); }
  .back svg, .home svg { width: 20px; height: 20px; }
  .title {
    font-family: 'Manrope', sans-serif;
    font-size: 16px; font-weight: 700;
    flex: 1; text-align: center;
  }
  .spacer { width: 36px; }
  .loading, .error-box { padding: 40px 16px; text-align: center; color: var(--text-mute); font-size: 13px; }
  .error-box { color: var(--danger); }

  .totals {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
    padding: 14px 16px;
  }
  .t-card {
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 12px 14px;
    box-shadow: var(--shadow-sm);
  }
  .t-label {
    font-size: 10px;
    color: var(--text-dim);
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .t-val {
    font-family: 'JetBrains Mono', monospace;
    font-size: 22px;
    font-weight: 700;
    margin-top: 6px;
    letter-spacing: -0.02em;
  }
  .t-card.success .t-val { color: var(--success); }
  .t-card.danger .t-val { color: var(--danger); }
  .t-card.accent .t-val { color: var(--brass); }

  .section-title {
    padding: 18px 16px 8px;
    font-family: 'Manrope', sans-serif;
    font-size: 11px; font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--text-dim);
  }

  .funnel { padding: 0 16px; display: flex; flex-direction: column; gap: 14px; }
  .stage { display: flex; flex-direction: column; gap: 4px; }
  .stage-head {
    display: flex; justify-content: space-between; align-items: baseline;
    font-size: 13px;
  }
  .s-name { font-family: 'Manrope', sans-serif; font-weight: 600; color: var(--text); }
  .s-count {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: var(--text-mute);
  }
  .bar-wrap {
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 8px;
    height: 16px;
    overflow: hidden;
  }
  .bar-wrap.thin { height: 6px; }
  .bar {
    height: 100%;
    border-radius: 7px;
    transition: width 240ms cubic-bezier(.18,.89,.32,1.05);
  }
  .bar.value { background: var(--ember); }
  .bar.count { background: var(--brass); opacity: 0.7; }

  .legend {
    padding: 10px 16px 0;
    font-size: 11px; color: var(--text-dim);
    display: flex; align-items: center; gap: 4px;
    flex-wrap: wrap;
  }
  .lg { display: inline-block; width: 12px; height: 6px; border-radius: 3px; vertical-align: middle; margin-right: 4px; }
  .lg-value { background: var(--ember); }
  .lg-count { background: var(--brass); opacity: 0.7; }

  .mgrs { padding: 0 16px; display: flex; flex-direction: column; gap: 10px; }
  .mgr-card {
    display: block;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 14px;
    color: var(--text);
    text-decoration: none;
    box-shadow: var(--shadow-sm);
    transition: transform 80ms ease;
  }
  .mgr-card:active { transform: scale(0.985); }
  .mgr-head {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 6px;
  }
  .m-name { font-family: 'Manrope', sans-serif; font-weight: 700; font-size: 14px; }
  .m-badge {
    background: var(--bg-elev-2);
    border: 1px solid var(--brass);
    border-radius: 999px;
    padding: 2px 6px;
    font-size: 9px; color: var(--brass);
    text-transform: uppercase; letter-spacing: 0.05em;
    font-weight: 600;
  }
  .mgr-bar-wrap {
    background: var(--bg-elev-2);
    border-radius: 6px;
    height: 8px;
    overflow: hidden;
    margin-bottom: 8px;
  }
  .mgr-bar {
    height: 100%;
    background: var(--ember);
    border-radius: 6px;
    transition: width 240ms cubic-bezier(.18,.89,.32,1.05);
  }
  .mgr-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
  }
  .m-stat { display: flex; flex-direction: column; gap: 2px; }
  .m-stat-val {
    font-family: 'JetBrains Mono', monospace;
    font-size: 14px; font-weight: 700;
    color: var(--text);
  }
  .m-stat-val.accent { color: var(--brass); }
  .m-stat-val.warn { color: var(--warning); }
  .m-stat-lab {
    font-size: 10px;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .footer-hint {
    padding: 24px 16px 8px;
    font-size: 11px;
    color: var(--text-dim);
    text-align: center;
    font-style: italic;
  }
  .hint {
    padding: 0 16px 8px;
    font-size: 12px;
    color: var(--text-mute);
  }
  .hint-note {
    color: var(--text-dim);
    font-size: 11px;
    font-style: italic;
  }

  .period-bar {
    padding: 0 16px 10px;
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    align-items: center;
  }
  .rng {
    background: var(--bg-elev-2);
    border: 1px solid var(--border);
    border-radius: 9px;
    padding: 6px 10px;
    color: var(--text-mute);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 120ms;
  }
  .rng:active { transform: scale(0.96); }
  .rng.active {
    background: var(--ember);
    color: #fff;
    border-color: var(--ember);
  }
  .custom-wrap {
    display: flex; align-items: center; gap: 4px;
    margin-left: 4px;
  }
  .custom-input {
    width: 56px;
    background: var(--bg-elev-2);
    border: 1px solid var(--border);
    border-radius: 9px;
    padding: 6px 8px;
    color: var(--text);
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    font-weight: 600;
    text-align: center;
  }
  .custom-input.active { border-color: var(--ember); color: var(--ember); }
  .custom-unit { color: var(--text-mute); font-size: 11px; }
  .loading-pill {
    background: var(--bg-elev-2);
    color: var(--text-mute);
    font-size: 10px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
    padding: 3px 8px;
    border-radius: 999px;
    margin-left: 8px;
  }

  .mf-list { padding: 0 16px; display: flex; flex-direction: column; gap: 12px; }
  .mf-card {
    display: block;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 14px;
    color: var(--text);
    text-decoration: none;
    box-shadow: var(--shadow-sm);
  }
  .mf-name {
    font-family: 'Manrope', sans-serif;
    font-weight: 700;
    font-size: 14px;
    margin-bottom: 10px;
  }
  .mf-funnel { display: flex; flex-direction: column; gap: 8px; }
  .mf-row {
    display: grid;
    grid-template-columns: 90px 32px 1fr auto;
    align-items: center;
    gap: 8px;
    font-size: 12px;
  }
  .mf-label { color: var(--text-mute); }
  .mf-val {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 700;
    text-align: right;
    color: var(--text);
  }
  .mf-bar {
    height: 8px;
    background: var(--bg-elev-2);
    border-radius: 4px;
    overflow: hidden;
  }
  .mf-fill {
    height: 100%;
    background: var(--ember);
    border-radius: 4px;
    transition: width 240ms ease;
  }
  .mf-fill.brass { background: var(--brass); }
  .mf-pct {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: var(--text-dim);
    white-space: nowrap;
  }
  .mf-row-bottom {
    display: flex; gap: 12px; flex-wrap: wrap;
    margin-top: 4px;
    padding-top: 8px;
    border-top: 1px solid var(--border);
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
  }
  .mf-stat.success { color: var(--success); }
  .mf-stat.danger { color: var(--danger); }
  .mf-stat.accent { color: var(--brass); font-weight: 700; }
  .mf-stat.dim { color: var(--text-dim); }
</style>
